import { useState, useEffect } from 'react';
import { Loader2, RefreshCw, Save, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { invoicingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const KNOWN_CHANNELS = ['airbnb', 'booking_com', 'direct_booking'];

export function ChannelMappingPanel({ entityId }) {
  const [customers, setCustomers] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mappingRes, customerRes] = await Promise.all([
        invoicingAPI.getChannelMapping(entityId),
        invoicingAPI.listCustomers(entityId),
      ]);
      setMappings(mappingRes.data.mappings || []);
      setCustomers(customerRes.data.customers || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (entityId) loadData();
  }, [entityId]);

  const syncCustomers = async () => {
    setSyncing(true);
    try {
      const res = await invoicingAPI.syncCustomers(entityId);
      toast.success(`Synced ${res.data.synced} customers from Pennylane`);
      // Reload customers
      const customerRes = await invoicingAPI.listCustomers(entityId);
      setCustomers(customerRes.data.customers || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const updateMapping = (channel, customerId) => {
    const customer = customers.find((c) => c.external_id === String(customerId));
    setMappings((prev) => {
      const existing = prev.filter((m) => m.channel !== channel);
      if (customerId) {
        existing.push({
          channel,
          pennylane_customer_id: Number(customerId),
          customer_name: customer?.name || '',
        });
      }
      return existing;
    });
  };

  const saveMappings = async () => {
    setSaving(true);
    try {
      await invoicingAPI.putChannelMapping(entityId, mappings);
      toast.success('Channel mappings saved');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const getMappingForChannel = (channel) =>
    mappings.find((m) => m.channel === channel);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Channel to Customer Mapping</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Map each booking channel to a Pennylane customer. Required before generating invoices.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={syncCustomers} disabled={syncing}>
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            Sync Customers
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {customers.length === 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3">
            <p className="text-sm flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              No customers synced. Click "Sync Customers" to fetch from Pennylane.
            </p>
          </div>
        )}

        {customers.length > 0 && (
          <>
            <div className="space-y-3">
              {KNOWN_CHANNELS.map((channel) => {
                const mapping = getMappingForChannel(channel);
                return (
                  <div key={channel} className="flex items-center gap-3">
                    <div className="w-36">
                      <Badge variant="outline" className="text-xs">
                        {channel}
                      </Badge>
                    </div>
                    <select
                      value={mapping?.pennylane_customer_id || ''}
                      onChange={(e) => updateMapping(channel, e.target.value)}
                      className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="">-- Select customer --</option>
                      {customers.map((c) => (
                        <option key={c.external_id} value={c.external_id}>
                          {c.name} (ID: {c.external_id})
                        </option>
                      ))}
                    </select>
                    {mapping ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={saveMappings} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save Mappings
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
